/**
 * Receiving `nostr:` URIs from the operating system.
 *
 * Two delivery paths, because a link can arrive before the app exists. The event
 * covers a running window; the parked URI covers the launch that happened *because*
 * of the link, which an event cannot reach — it would be emitted into a window with
 * no listener yet.
 *
 * Nothing here decides where a URI goes: `resolveNostrUri` does, and it is pure and
 * tested. This file is the wiring.
 */

import { useEffect, useRef } from "react";
import type { Route } from "../shell/routes";
import { resolveNostrUri } from "./nostrUri";
import { invokeNative, isDesktopShell, listenNative } from "./runtime";

/** Must match `URI_EVENT` in `src-tauri/src/deep_link.rs`. */
const URI_EVENT = "setu://nostr-uri";

export interface DeepLinkTargets {
  onNavigate(route: Route): void;
  onOpenThread(id: string): void;
}

/**
 * Open whatever the OS asks Setu to open.
 *
 * The handlers are read through a ref so the subscription is installed exactly
 * once. Depending on them directly would tear down and reinstall the listener on
 * every render of the shell — and a URI arriving in the gap would be delivered to
 * nobody.
 */
export function useNostrDeepLinks(targets: DeepLinkTargets): void {
  const latest = useRef(targets);
  latest.current = targets;

  useEffect(() => {
    if (!isDesktopShell()) return;

    const open = (uri: string) => {
      const target = resolveNostrUri(uri);
      // Silence is the correct response to a URI that addresses nothing — see
      // `nostrUri.ts` for why guessing is worse than doing nothing.
      if (target === undefined) return;
      if (target.kind === "thread") {
        latest.current.onOpenThread(target.id);
        return;
      }
      latest.current.onNavigate(target.route);
    };

    // Listener first: a link that arrives during the round trip below would
    // otherwise fall between the two paths and be lost.
    const stop = listenNative<string>(URI_EVENT, open);

    void invokeNative<string | null>("take_launch_nostr_uri").then((uri) => {
      if (typeof uri === "string") open(uri);
    });

    return stop;
  }, []);
}
